import { useEffect, useRef } from 'react';

import { push } from 'connected-react-router';
import { getAllCountCampaigns, isAccountStatusReadOnly } from 'utils/clickme';
import { CREATE_MENU_PAGE_ROUTE } from 'utils/constants/app';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';
import useSendUXFBEvent from 'utils/uxfeedback/hooks/useSendUXFBEvent';

import TitlePage from 'components/TitlePage';

import { ArchiveCampaignAlert } from 'components/CampaignAdminActions/ArchiveCampaignAlert/ArchiveCampaignAlert';
import { RemainderBudgetsModal } from 'components/CampaignAdminActions/RemainderBudgetsModal/RemainderBudgetsModal';
import { SourceAccountsModal } from 'components/CampaignAdminActions/SourceAccountsModal/SourceAccountsModal';
import BalanceReplenishmentModal from 'components/common/BalanceReplenishmentModal/BalanceReplenishmentModal';
import { ChangeHistoryModal } from 'components/common/ChangeHistoryModal/ChangeHistoryModal';
import NeedToReplenishAlert from 'components/common/NeedToReplenishAlert/NeedToReplenishAlert';
import CampaignList from 'components/Dashboard/CampaignList';
import DashboardButtons from 'components/Dashboard/DashboardButtons/DashboardButtons';
import useHasAppliedFilters from 'components/Dashboard/Filters/useHasAppliedFilters';
import MultiActions from 'components/Dashboard/MultiActions/MultiActions';
import TabsStatus from 'components/Dashboard/TabsStatus';
import { WebinarBanner } from 'components/Dashboard/WebinarBanner/WebinarBanner';
import { WEBINAR_BANNER_CONFIG } from 'components/Dashboard/WebinarBanner/webinarBannerConfig';
import { YandexVideoBanner } from 'components/Dashboard/YandexVideoBanner/YandexVideoBanner';

import { selectIsWebinarBannerVisible, setScrollToAds } from 'store/autoReducers';
import { CampaignLoadState } from 'store/models/dashboard';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    title: 'Dashboard.title',
};

export const Dashboard = () => {
    const counts = useSelector((state) => state.countsCampaign);
    const scrollToMyAds = useSelector((state) => state.scrollToMyAds);
    const isLoading = useSelector((state) => state.dashboard.isLoad === CampaignLoadState.Loading);
    const currentAccountStatus = useSelector((state) => state.user.user?.currentAccount.status);
    const campaignSearchValue = useSelector((state) => state.dashboard.campaignSearchValue);
    const isCampaignSearchLoading = useSelector((state) => state.dashboard.isCampaignSearchLoading);
    const isCampaignSearchUploaded = useSelector((state) => state.dashboard.isCampaignSearchUploaded);
    const isWebinarBannerVisible = useSelector(selectIsWebinarBannerVisible);

    const [
        isBalanceReplenishmentModalVisible,
        setIsBalanceReplenishmentModalVisibleOn,
        setIsBalanceReplenishmentModalVisibleOff,
    ] = useOnOffState(false);

    const hasSearch = campaignSearchValue || isCampaignSearchLoading || isCampaignSearchUploaded;
    const hasAppliedFilters = useHasAppliedFilters();

    const dispatch = useDispatch();
    const myAdsRef = useRef(null);
    const trl = useTrls();

    const haveAnyCampaigns = getAllCountCampaigns(counts) > 0;

    useEffect(() => {
        if (scrollToMyAds && myAdsRef?.current) {
            myAdsRef.current.scrollIntoView();
            setScrollToAds(false);
        }
    }, [scrollToMyAds]);

    useEffect(() => {
        if (hasSearch || hasAppliedFilters || isLoading) {
            return;
        }
        if (!haveAnyCampaigns && !isAccountStatusReadOnly(currentAccountStatus)) {
            dispatch(push(CREATE_MENU_PAGE_ROUTE));
        }
    }, [currentAccountStatus, dispatch, hasAppliedFilters, hasSearch, haveAnyCampaigns, isLoading]);

    useSendUXFBEvent('create_campaign');

    return (
        <>
            <WebinarBanner {...WEBINAR_BANNER_CONFIG} />
            {!isWebinarBannerVisible && <YandexVideoBanner />}
            <TitlePage ref={myAdsRef} header={trl(TrlKeys.title)} />
            <DashboardButtons />
            <TabsStatus />
            <MultiActions />
            <CampaignList />
            <ChangeHistoryModal />
            <RemainderBudgetsModal />
            <SourceAccountsModal />
            <ArchiveCampaignAlert />
            <NeedToReplenishAlert openBalanceReplenishmentModal={setIsBalanceReplenishmentModalVisibleOn} />
            <BalanceReplenishmentModal
                isVisible={isBalanceReplenishmentModalVisible}
                closeModal={setIsBalanceReplenishmentModalVisibleOff}
            />
        </>
    );
};
